import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  FloppyDisk,
  Tag,
  Percent,
  CurrencyDollar,
  Gift,
  Truck,
  MagnifyingGlass,
  X,
  Check,
  CircleNotch,
} from '@phosphor-icons/react';
import { useDiscount, useCreateDiscount, useUpdateDiscount } from '../../hooks/useDiscounts';
import { useProducts } from '../../hooks/useProducts';
import { useCategories } from '../../hooks/useCategories';
import {
  DiscountType,
  DiscountScope,
  CreateDiscountDto,
  UpdateDiscountDto,
} from '../../types/discount';
import { ProductStatus } from '@dukene/shared';

interface DiscountFormData {
  name: string;
  description: string;
  code: string;
  type: DiscountType;
  value: number;
  scope: DiscountScope;
  minimumPurchase?: number;
  maximumDiscount?: number;
  usageLimit?: number;
  perCustomerLimit?: number;
  startDate: string;
  endDate?: string;
  active: boolean;
  productIds?: string[];
  categoryIds?: string[];
}

const initialFormData: DiscountFormData = {
  name: '',
  description: '',
  code: '',
  type: DiscountType.PERCENTAGE,
  value: 0,
  scope: DiscountScope.STORE_WIDE,
  startDate: new Date().toISOString().split('T')[0],
  active: true,
};

export default function DiscountForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<DiscountFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  const { data: existingDiscount, isLoading: isLoadingDiscount } = useDiscount(id || '');
  const { data: productsData } = useProducts({
    search: productSearch,
    status: ProductStatus.ACTIVE,
    limit: 20,
  });
  const { data: categoriesData } = useCategories({
    limit: 20,
  });

  const createMutation = useCreateDiscount();
  const updateMutation = useUpdateDiscount();

  useEffect(() => {
    if (existingDiscount) {
      const pIds =
        existingDiscount.products?.map((p: any) => p.productId || p.id) || [];
      setFormData({
        name: existingDiscount.name,
        description: existingDiscount.description || '',
        code: existingDiscount.code || '',
        type: existingDiscount.type,
        value: existingDiscount.value,
        scope: existingDiscount.scope,
        minimumPurchase: existingDiscount.minimumPurchase || undefined,
        maximumDiscount: existingDiscount.maximumDiscount || undefined,
        usageLimit: existingDiscount.usageLimit || undefined,
        perCustomerLimit: existingDiscount.perCustomerLimit || undefined,
        startDate: existingDiscount.startDate.split('T')[0],
        endDate: existingDiscount.endDate ? existingDiscount.endDate.split('T')[0] : undefined,
        active: existingDiscount.active,
        productIds: pIds,
        categoryIds: (existingDiscount as any).categoryIds || [],
      });
    }
  }, [existingDiscount]);

  const handleChange = (field: keyof DiscountFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const n = { ...prev };
        delete n[field];
        return n;
      });
    }
  };

  const generateCode = () => {
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    handleChange('code', `SAVE-${random}`);
  };

  const handleProductToggle = (productId: string) => {
    setFormData((prev) => {
      const current = prev.productIds || [];
      const exists = current.includes(productId);
      return {
        ...prev,
        productIds: exists ? current.filter((i) => i !== productId) : [...current, productId],
      };
    });
  };

  const handleCategoryToggle = (categoryId: string) => {
    setFormData((prev) => {
      const current = prev.categoryIds || [];
      const exists = current.includes(categoryId);
      return {
        ...prev,
        categoryIds: exists ? current.filter((i) => i !== categoryId) : [...current, categoryId],
      };
    });
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Discount title is required';
    if (!formData.code.trim()) newErrors.code = 'Discount code is required';
    if (formData.type !== DiscountType.FREE_SHIPPING && (!formData.value || formData.value <= 0)) {
      newErrors.value = 'Discount value must be greater than 0';
    }
    if (formData.type === DiscountType.PERCENTAGE && formData.value > 100) {
      newErrors.value = 'Percentage cannot exceed 100%';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const basePayload = {
        name: formData.name,
        description: formData.description,
        code: formData.code.toUpperCase(),
        type: formData.type,
        value: Number(formData.value),
        scope: formData.scope,
        minimumPurchase: formData.minimumPurchase ? Number(formData.minimumPurchase) : undefined,
        maximumDiscount: formData.maximumDiscount ? Number(formData.maximumDiscount) : undefined,
        usageLimit: formData.usageLimit ? Number(formData.usageLimit) : undefined,
        perCustomerLimit: formData.perCustomerLimit
          ? Number(formData.perCustomerLimit)
          : undefined,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
        active: formData.active,
        productIds:
          formData.scope === DiscountScope.SPECIFIC_PRODUCTS ? formData.productIds : undefined,
        categoryIds:
          formData.scope === DiscountScope.CATEGORIES ? formData.categoryIds : undefined,
      };

      if (isEditMode && id) {
        await updateMutation.mutateAsync({ id, data: basePayload as UpdateDiscountDto });
      } else {
        await createMutation.mutateAsync(basePayload as CreateDiscountDto);
      }
      navigate('/discounts');
    } catch (err) {
      console.error('Failed to save discount:', err);
    }
  };

  const products: any[] = Array.isArray(productsData)
    ? productsData
    : productsData?.products || [];
  const categories: any[] = Array.isArray(categoriesData) ? categoriesData : [];

  if (isEditMode && isLoadingDiscount) {
    return (
      <div className="flex items-center justify-center p-16 text-xs text-[#5C5F62]">
        <CircleNotch weight="bold" className="w-6 h-6 animate-spin mr-2 text-[#006838]" />
        Loading discount details...
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-6xl">
      {/* Polaris High-Density Top Bar */}
      <div className="flex items-center justify-between gap-3 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/discounts')}
            className="p-1.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-base font-bold text-[#202223] dark:text-white">
              {isEditMode ? 'Edit Discount Code' : 'Create Discount Code'}
            </h1>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400">
              Configure promotional coupons, automatic rules, and checkout savings
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/discounts')}
            className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white transition-colors"
          >
            Discard
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <CircleNotch weight="bold" className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
            )}
            {isEditMode ? 'Save Changes' : 'Create Discount'}
          </button>
        </div>
      </div>

      {/* 2-Column Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Main Details (2 columns) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Discount Code & Name */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62]">
              Amount & Identification
            </h3>

            <div>
              <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                Discount Code <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag
                    weight="bold"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
                  />
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                    placeholder="e.g. SUMMER20, WELCOME10"
                    className="w-full h-9 pl-9 pr-3 uppercase font-mono font-bold rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                  />
                </div>
                <button
                  type="button"
                  onClick={generateCode}
                  className="h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white transition-colors shrink-0"
                >
                  Generate Code
                </button>
              </div>
              {errors.code && <p className="text-xs text-red-600 mt-1">{errors.code}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                  Internal Campaign Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g. Summer Sale 20% Off"
                  className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                />
                {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                  Customer Description (Optional)
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="e.g. Applicable on summer collection"
                  className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                />
              </div>
            </div>
          </div>

          {/* Discount Type Selector */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62]">
              Discount Type & Value
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {[
                {
                  id: DiscountType.PERCENTAGE,
                  label: 'Percentage',
                  desc: '% off items',
                  icon: Percent,
                },
                {
                  id: DiscountType.FIXED_AMOUNT,
                  label: 'Fixed Amount',
                  desc: 'KES off total',
                  icon: CurrencyDollar,
                },
                {
                  id: DiscountType.BUY_X_GET_Y,
                  label: 'Buy X Get Y',
                  desc: 'BOGO promotions',
                  icon: Gift,
                },
                {
                  id: DiscountType.FREE_SHIPPING,
                  label: 'Free Shipping',
                  desc: 'Waive delivery fee',
                  icon: Truck,
                },
              ].map((t) => {
                const Icon = t.icon;
                const isSelected = formData.type === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleChange('type', t.id)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      isSelected
                        ? 'border-[#006838] ring-1 ring-[#006838] bg-[#E3F1DF]/20'
                        : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196]'
                    }`}
                  >
                    <Icon
                      weight="bold"
                      className={`w-4 h-4 mb-1.5 ${
                        isSelected ? 'text-[#006838]' : 'text-[#5C5F62]'
                      }`}
                    />
                    <span className="block text-xs font-bold text-[#202223] dark:text-white">
                      {t.label}
                    </span>
                    <span className="block text-[10px] text-[#5C5F62]">{t.desc}</span>
                  </button>
                );
              })}
            </div>

            {formData.type !== DiscountType.FREE_SHIPPING && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                    {formData.type === DiscountType.PERCENTAGE
                      ? 'Discount Percentage (%)'
                      : 'Discount Value (KES)'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={formData.value}
                    onChange={(e) => handleChange('value', parseFloat(e.target.value) || 0)}
                    className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-mono font-bold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                  />
                  {errors.value && <p className="text-xs text-red-600 mt-1">{errors.value}</p>}
                </div>

                {formData.type === DiscountType.PERCENTAGE && (
                  <div>
                    <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                      Max Discount Cap (KES, Optional)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.maximumDiscount || ''}
                      onChange={(e) =>
                        handleChange(
                          'maximumDiscount',
                          e.target.value ? parseFloat(e.target.value) : undefined
                        )
                      }
                      placeholder="No limit"
                      className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-mono text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Applicability / Scope */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62]">
              Applies To (Scope)
            </h3>

            <div className="grid grid-cols-3 gap-2.5">
              {[
                {
                  id: DiscountScope.STORE_WIDE,
                  label: 'All Products',
                  desc: 'Entire catalog',
                },
                {
                  id: DiscountScope.SPECIFIC_PRODUCTS,
                  label: 'Specific Products',
                  desc: 'Selected items only',
                },
                {
                  id: DiscountScope.CATEGORIES,
                  label: 'Specific Categories',
                  desc: 'Selected collections',
                },
              ].map((s) => {
                const isSelected = formData.scope === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => handleChange('scope', s.id)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      isSelected
                        ? 'border-[#006838] ring-1 ring-[#006838] bg-[#E3F1DF]/20'
                        : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196]'
                    }`}
                  >
                    <span className="block text-xs font-bold text-[#202223] dark:text-white">
                      {s.label}
                    </span>
                    <span className="block text-[10px] text-[#5C5F62]">{s.desc}</span>
                  </button>
                );
              })}
            </div>

            {/* Specific Product Picker */}
            {formData.scope === DiscountScope.SPECIFIC_PRODUCTS && (
              <div className="space-y-2 pt-2 border-t border-[#E1E4E8] dark:border-slate-800">
                <span className="text-xs font-semibold text-[#202223] dark:text-white block">
                  Select Qualifying Products ({formData.productIds?.length || 0} selected)
                </span>

                <div className="relative">
                  <MagnifyingGlass
                    weight="bold"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
                  />
                  <input
                    type="text"
                    placeholder="Search products to add..."
                    value={productSearch}
                    onFocus={() => setShowProductDropdown(true)}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      setShowProductDropdown(true);
                    }}
                    className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
                  />
                  {showProductDropdown && (
                    <div className="absolute left-0 right-0 top-9 z-20 max-h-48 overflow-y-auto bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-lg divide-y divide-[#E1E4E8] dark:divide-slate-800">
                      {products.map((prod: any) => {
                        const selected = (formData.productIds || []).includes(prod.id);
                        return (
                          <button
                            key={prod.id}
                            type="button"
                            onClick={() => handleProductToggle(prod.id)}
                            className="w-full p-2.5 text-left text-xs flex items-center justify-between hover:bg-[#F6F6F7] dark:hover:bg-slate-800"
                          >
                            <span className="font-semibold text-[#202223] dark:text-white">
                              {prod.title}
                            </span>
                            {selected && (
                              <Check weight="bold" className="w-4 h-4 text-[#006838]" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {formData.productIds && formData.productIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {formData.productIds.map((pid) => {
                      const p = products.find((x: any) => x.id === pid);
                      return (
                        <span
                          key={pid}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 border border-[#D2D5D9] dark:border-slate-700 text-xs font-medium text-[#202223] dark:text-white"
                        >
                          {p ? p.title : pid}
                          <button
                            type="button"
                            onClick={() => handleProductToggle(pid)}
                            className="text-[#5C5F62] hover:text-red-500"
                          >
                            <X weight="bold" className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Specific Category Picker */}
            {formData.scope === DiscountScope.CATEGORIES && (
              <div className="space-y-2 pt-2 border-t border-[#E1E4E8] dark:border-slate-800">
                <span className="text-xs font-semibold text-[#202223] dark:text-white block">
                  Select Qualifying Categories ({formData.categoryIds?.length || 0} selected)
                </span>

                <div className="flex flex-wrap gap-2">
                  {categories.map((cat: any) => {
                    const selected = (formData.categoryIds || []).includes(cat.id);
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleCategoryToggle(cat.id)}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${
                          selected
                            ? 'border-[#006838] bg-[#E3F1DF]/30 text-[#006838]'
                            : 'border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7]'
                        }`}
                      >
                        {selected && <Check weight="bold" className="w-3.5 h-3.5" />}
                        {cat.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar (Summary & Settings) */}
        <div className="space-y-4">
          {/* Summary Card */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62]">
              Discount Summary
            </h3>

            <div className="p-3 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold text-sm text-[#006838]">
                  {formData.code || 'CODE'}
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#E3F1DF] text-[#006838]">
                  {formData.active ? 'ACTIVE' : 'DRAFT'}
                </span>
              </div>
              <p className="text-xs font-bold text-[#202223] dark:text-white">
                {formData.name || 'Untitled Promo'}
              </p>
              <p className="text-xs text-[#5C5F62]">
                {formData.type === DiscountType.PERCENTAGE
                  ? `${formData.value || 0}% Off`
                  : formData.type === DiscountType.FIXED_AMOUNT
                  ? `KES ${formData.value || 0} Off`
                  : formData.type}{' '}
                — {formData.scope.replace(/_/g, ' ')}
              </p>
            </div>

            {/* Active Switch */}
            <div className="flex items-center justify-between pt-2 border-t border-[#E1E4E8] dark:border-slate-800">
              <span className="text-xs font-bold text-[#202223] dark:text-white">
                Enable Discount Code
              </span>
              <button
                type="button"
                onClick={() => handleChange('active', !formData.active)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  formData.active ? 'bg-[#006838]' : 'bg-[#D2D5D9] dark:bg-slate-700'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    formData.active ? 'translate-x-4.5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Dates & Limits */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62]">
              Schedule & Limits
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                  End Date (Optional)
                </label>
                <input
                  type="date"
                  value={formData.endDate || ''}
                  onChange={(e) => handleChange('endDate', e.target.value || undefined)}
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                  Min Purchase Requirement (KES)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.minimumPurchase || ''}
                  onChange={(e) =>
                    handleChange(
                      'minimumPurchase',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                  placeholder="None required"
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-mono text-[#202223] dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                  Total Redemption Usage Limit
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.usageLimit || ''}
                  onChange={(e) =>
                    handleChange(
                      'usageLimit',
                      e.target.value ? parseInt(e.target.value, 10) : undefined
                    )
                  }
                  placeholder="Unlimited redemptions"
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-mono text-[#202223] dark:text-white"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
